import Phaser from "phaser";
import type { RewardEffect, RewardPayload } from "@/core";
import type { GameApp } from "../app/GameApp";
import { REGISTRY_KEYS } from "../app/GameApp";
import { mountOverlay, type DomOverlay } from "@/ui/domHelpers";
import { EQUIPMENT_CATALOG } from "@/core/roscha/equipment";

const STAT_LABEL: Record<string, string> = {
  strength: "Сила",
  dexterity: "Ловкость",
  intelligence: "Интеллект",
  wisdom: "Мудрость",
  constitution: "Телосложение",
};

const RESOURCE_LABEL: Record<string, string> = {
  gold: "Золото",
  mana: "Мана",
};

function describeEffect(effect: RewardEffect): { label: string; description?: string } {
  switch (effect.type) {
    case "resource":
      return { label: `+${effect.amount} ${RESOURCE_LABEL[effect.resourceType] ?? effect.resourceType}` };
    case "equipment": {
      const eq = EQUIPMENT_CATALOG[effect.equipmentId];
      return { label: eq?.name ?? effect.equipmentId, description: eq?.description };
    }
    case "hp":
      return { label: `+${effect.amount} HP` };
    case "stat": {
      const sign = effect.delta >= 0 ? "+" : "";
      return { label: `${sign}${effect.delta} к ${STAT_LABEL[effect.stat] ?? effect.stat}` };
    }
    case "energy":
      return { label: `+${effect.amount} Энергии` };
    case "xp":
      return { label: `+${effect.amount} XP` };
  }
}

export class EventScene extends Phaser.Scene {
  private app!: GameApp;
  private overlay?: DomOverlay;

  constructor() {
    super({ key: "EventScene" });
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    const initial = this.registry.get(REGISTRY_KEYS.currentEvent) as RewardPayload | null;
    if (initial) this.render(initial);

    const onChange = (_parent: unknown, value: RewardPayload | null) => {
      if (value) this.render(value);
    };
    this.registry.events.on(`changedata-${REGISTRY_KEYS.currentEvent}`, onChange);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.registry.events.off(`changedata-${REGISTRY_KEYS.currentEvent}`, onChange);
    });
  }

  private render(eventData: RewardPayload) {
    const imageHtml = eventData.image
      ? `<div class="event__image-frame"><img class="event__image" src="${eventData.image}" alt=""></div>`
      : "";

    let rewardHtml = "";
    const effects = eventData.effects && eventData.effects.length > 0
      ? eventData.effects
      : (eventData.effect ? [eventData.effect] : []);
    if (effects.length > 0) {
      const items = effects.map((eff) => {
        const d = describeEffect(eff);
        return `<li><span class="event__reward-name">${d.label}</span>${d.description ? ` · <span class="event__reward-desc">${d.description}</span>` : ""}</li>`;
      }).join("");
      rewardHtml = `
        <div class="event__reward">
          <span class="event__reward-label">${effects.length > 1 ? "Награды" : "Награда"}</span>
          <ul class="event__reward-list">${items}</ul>
        </div>
      `;
    } else if (eventData.reward) {
      rewardHtml = `
        <div class="event__reward">
          <span class="event__reward-label">Награда</span>
          <span class="event__reward-name">${eventData.reward.itemName}</span>
        </div>
      `;
    }

    const bodyHtml = `
      ${imageHtml}
      <p class="event__text">${eventData.text ?? "Ты нашёл что-то интересное..."}</p>
      ${rewardHtml}
      <div class="event__actions">
        <button class="dom-btn dom-btn--primary" data-action="continue">Продолжить</button>
      </div>
    `;

    this.overlay = mountOverlay(this, {
      title: "Событие",
      bodyHtml,
      onClose: () => this.app.finishRewardEvent(),
    });

    this.overlay.root.querySelector('[data-action="continue"]')?.addEventListener("click", () => {
      this.app.finishRewardEvent();
    });
  }

  shutdown() {
    this.overlay = undefined;
  }
}
